import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { FadeIn, Reveal } from "../components/Reveal";
import { MetaLabel } from "../components/Bits";
import { IMAGES } from "../lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Estate Management Services",
  "Il Salotto Digitale — Courses",
  "Tools & Guides",
  "I'm just starting out",
  "General enquiry",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", interest: INTERESTS[0], message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = "Contact · Marella Bolognesi — Miami, Florida"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — your message has been received.");
      setDone(true);
      setForm({ name: "", email: "", interest: INTERESTS[0], message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-20">
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 pt-20 lg:pt-28 pb-24 grid lg:grid-cols-12 gap-16">
        {/* Left */}
        <div className="lg:col-span-5">
          <FadeIn><MetaLabel>Contact · Miami, Florida</MetaLabel></FadeIn>
          <FadeIn delay={0.08}>
            <h1 className="mt-6 font-serif font-light text-ink text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Start a conversation.
            </h1>
          </FadeIn>
          <FadeIn delay={0.16}>
            <p className="mt-8 text-lg font-light text-ink-soft leading-relaxed max-w-md">
              Whether you're a principal or family office considering private Estate Management, or an aspiring Estate Manager wondering where to begin — tell me a little about what you're looking for, and I'll point you in the right direction.
            </p>
          </FadeIn>
          <FadeIn delay={0.24}>
            <div className="mt-12 overflow-hidden">
              <img src={IMAGES.livingWell} alt="Living well" className="w-full aspect-[4/3] object-cover" />
            </div>
          </FadeIn>
        </div>

        {/* Form */}
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal>
            {done ? (
              <div data-testid="contact-success" className="border border-line p-10 lg:p-16 text-center bg-surface">
                <p className="font-serif text-4xl text-ink">Thank you.</p>
                <p className="mt-4 text-ink-soft font-light">Your message is on its way to Marella. You'll hear back soon.</p>
                <button onClick={() => setDone(false)} data-testid="contact-reset" className="mt-8 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Name</label>
                    <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="mt-3 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="Your full name" />
                  </div>
                  <div>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Email</label>
                    <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className="mt-3 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light transition-colors" placeholder="you@email.com" />
                  </div>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">I'm interested in</label>
                  <div className="mt-4 flex flex-wrap gap-3">
                    {INTERESTS.map((it) => (
                      <button type="button" key={it} data-testid={`interest-${it.slice(0, 6).toLowerCase().replace(/\s+/g, "")}`}
                        onClick={() => setForm({ ...form, interest: it })}
                        className={`px-4 py-2.5 text-[11px] uppercase tracking-[0.15em] border transition-colors ${form.interest === it ? "bg-ink text-bg border-ink" : "border-line text-ink-soft hover:border-ink"}`}>
                        {it}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[11px] uppercase tracking-[0.2em] text-ink-soft">Message</label>
                  <textarea data-testid="contact-message" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                    className="mt-3 w-full bg-transparent border-b border-line focus:border-ink outline-none py-3 text-ink text-lg font-light resize-none transition-colors" placeholder="Tell me a little about what you're looking for…" />
                </div>

                <button type="submit" disabled={loading} data-testid="contact-submit"
                  className="group w-full sm:w-auto bg-ink text-bg px-12 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#33312E] transition-colors flex items-center justify-center gap-3 disabled:opacity-60">
                  {loading ? "Sending…" : "Send Message"}
                  <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
